import { MAX_SAMPLES_PER_PACK, SamplePack } from '@arpeggi/shared'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { useArpMedias } from '../../../../queries/arpMedia'
import Avatar from '../../../core/Avatar'
import Button from '../../../core/Button/Button'
import CenteredLoadingAnimation from '../../../core/CenteredLoadingAnimation'
import { Box, Spacer } from '../../../core/Components'
import { Flex } from '../../../core/Flex'
import { ChevronDown, HeartOutlined } from '../../../core/icons'
import Text from '../../../core/Text/Text'
import SampleList from '../sample-list'

interface Props {
  samplePack: SamplePack
  isActive: boolean
  handleSelect: () => void
}

export enum LoadingStatus {
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  IDLE = 'IDLE',
  LOADING = 'LOADING'
}

const useSamplePackDropDown = (samplePackId: number) => {
  const perPage = MAX_SAMPLES_PER_PACK
  const [status, setStatus] = useState<LoadingStatus>(LoadingStatus.LOADING)
  const [registrySamples, setRegistrySamples] = useState<IArpMedia[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const samplePackSoundsQuery = useArpMedias({ samplePackId, perPage })

  useEffect(() => {
    const loadSamples = async () => {
      setStatus(LoadingStatus.LOADING)

      const result = await ArpRegistry.getByIdsAsync(
        samplePackSoundsQuery.data?.pages.flatMap((page) => page.data.map((sample) => sample.id))
      )
      setRegistrySamples(result)

      setStatus(LoadingStatus.SUCCESS)
    }

    if (samplePackSoundsQuery.data) {
      loadSamples()
    }
  }, [samplePackSoundsQuery.data])

  const toggle = () => setIsOpen((prev) => !prev)

  return { status, registrySamples, isOpen, toggle, samplePackSoundsQuery }
}

export const SamplePackDropdown: React.FC<Props> = ({ samplePack, isActive, handleSelect }) => {
  const { status, registrySamples, isOpen, toggle, samplePackSoundsQuery } = useSamplePackDropDown(
    samplePack.id
  )

  return (
    <>
      <Flex
        css={{
          gap: '8px',
          paddingTop: '$10',
          paddingBottom: '$10',
          alignItems: 'center',
          color: '$gray06',
          '&:hover': {
            cursor: 'pointer'
          }
        }}
        onClick={() => {
          toggle()
          handleSelect()
        }}
      >
        <Avatar imageUrl={samplePack.coverImage.url} rounded size="32px" />
        <Text size="14" css={{ color: '$gray06 ' }}>
          {samplePack.title}
        </Text>
        <Spacer />
        <Text css={{ color: '$gray06 ' }}>{samplePack.soundFavoriteCount}</Text>
        <HeartOutlined />
        <ChevronDown />
      </Flex>

      {isOpen && (
        <Box css={{ paddingLeft: '$10', paddingRight: '$10' }}>
          <Text
            size="14"
            css={{
              color: '$gray07',
              marginTop: '$10',
              marginBottom: '$10',
              display: '-webkit-box',
              '-webkit-line-clamp': 2,
              '-webkit-box-orient': 'vertical',
              overflow: 'hidden'
            }}
          >
            {samplePack.description}
          </Text>
          {status === LoadingStatus.LOADING && <CenteredLoadingAnimation />}
          {status === LoadingStatus.SUCCESS && (
            <SampleList
              samples={registrySamples}
              defaultIsOpen
              defaultIsActive={isActive}
              infiniteScroll={{
                hasMore: samplePackSoundsQuery.hasNextPage,
                next: samplePackSoundsQuery.fetchNextPage,
                dataLength: samplePackSoundsQuery?.data.pages[0].meta.total
              }}
            />
          )}
          <Box css={{ marginLeft: '12px' }}>
            <Link to={`/sample-packs/${samplePack.id}/`}>
              <Button
                type="dark-outline"
                bigSquare
                css={{ width: '100%', marginTop: '$10', marginBottom: '$10' }}
              >
                Open Sample Pack Details
              </Button>
            </Link>
          </Box>
        </Box>
      )}
    </>
  )
}
